import type React from 'react';
import { useCallback, useEffect, useMemo, useState } from 'react';

import { getUserData, toResultType } from '../../../constants/global';
import { useGlobalContext } from '../../../context/global';
import { usePeerChat } from '../../../hooks/usePeerChat';
import { type IAgentAssignmentItem } from '../../../interface/agent-assignment';
import { type IAgentChatPanelData, type ISupportChatPanelData } from '../../../interface/peer-chat';
import { type ChatCounterpart } from '../../../interface/support-chat';
import { getAssignmentsByEntity } from '../../../lib/agent-assignment-api';
import { hierarchyLevelOfCode } from '../../../lib/entity-code';
import {
	contextLine,
	type ConversationUnitState,
	emptyStateFor,
	escalationDraft,
	INACTIVE_HINT,
	type PeerRole,
	presenceLine,
	quickMessages,
	reassignedNotice,
	roleLabelFor,
	unitStateChip,
} from '../../../lib/peer-chat-copy';
import { fetchPeerPresence, isOwnMessage } from '../../../lib/support-api';
import { type IChildAgentData } from '../../pages/child-agent';
import { agentDisplayName, formatPhoneForWa } from '../../pages/child-agent/utils';
import { deriveCoverage } from '../../pages/overview/quick-analytics/utils';
import { useCopy } from '../../utils/hooks';
import { ActionSheet, type IActionSheetItem } from '../../utils/message-action-sheet';
import ChatConversation from './ChatConversation';
import ChatHeaderStrip from './ChatHeaderStrip';

/** The unit's live state is refreshed while the conversation is open; it is why anyone is messaging. */
const COVERAGE_REFRESH_MS = 60_000;
const PRESENCE_REFRESH_MS = 30_000;

const AgentSkeleton = () => (
	<div
		className='support-chat sc-loading'
		aria-busy='true'
	>
		<span className='visually-hidden'>Opening the conversation…</span>
	</div>
);

/**
 * One parent ↔ child conversation.
 *
 * <p>A view over the engine the layout hosts (see `PeerThreadHosts`), plus the three things a
 * support conversation does not have: an identity strip, a pinned result-state chip, and quick
 * messages tuned to who is writing and what the unit is doing. Everything inside the transcript
 * is {@link ChatConversation}, exactly as the desk thread uses it.</p>
 */
const AgentChatPanel = () => {
	const { rsProps, states, supportSession } = useGlobalContext();
	const data = (rsProps?.data ?? null) as IAgentChatPanelData | null;
	const counterpart = data?.counterpart;
	const electionId = data?.electionId ?? 0;
	// `counterpart.role` is THEIR relation to the reader; the reader is the other one.
	const role: PeerRole = counterpart?.role === 'child' ? 'parent' : 'child';
	const me = supportSession?.me ?? Number(getUserData().user?.UserId ?? 0);

	const thread = usePeerChat(counterpart?.coreUserId ?? 0, electionId);

	// ── The unit's live state ───────────────────────────────────────────────────────────────
	// Parent side: the counterpart's unit, from the same read Agent Coverage uses. Child side:
	// the reader's own assignment, which the layout already fetched.
	const electionOfficial = states?._election?.get_ElectionOfficial?.data?.find((i) => i.userId === me);
	const [item, setItem] = useState<IAgentAssignmentItem | null>(null);
	const [coverageState, setCoverageState] = useState<'idle' | 'loading' | 'ready' | 'error'>('idle');

	// The unit this conversation is about: the child's own unit on both sides.
	const unitCode = role === 'child' ? electionOfficial?.assignment?.code : counterpart?.entityCode;
	const watchCode = unitCode;

	useEffect(() => {
		if (role === 'child' || !watchCode || !electionId) return;
		let cancelled = false;
		const load = () => {
			setCoverageState((s) => (s === 'idle' ? 'loading' : s));
			void getAssignmentsByEntity([watchCode], electionId).then((outcome) => {
				if (cancelled) return;
				if (!outcome.reachable || outcome.httpStatus < 200 || outcome.httpStatus >= 300) {
					setCoverageState('error');
					return;
				}
				setItem(outcome.body?.data?.items?.[0] ?? null);
				setCoverageState('ready');
			});
		};
		load();
		const timer = window.setInterval(load, COVERAGE_REFRESH_MS);
		return () => {
			cancelled = true;
			window.clearInterval(timer);
		};
	}, [role, watchCode, electionId]);

	const coverage = item ? deriveCoverage(item) : null;
	const unitState: ConversationUnitState = useMemo(() => {
		if (role === 'child') return electionOfficial?.assignment?.isCompleted ? 'uploaded' : 'pending';
		switch (coverage?.state) {
			case 'uploaded':
				return 'uploaded';
			case 'conflict':
				return 'conflict';
			case 'pending':
			case 'agentUnavailable':
				return 'pending';
			default:
				return 'unknown';
		}
	}, [role, coverage?.state, electionOfficial?.assignment?.isCompleted]);
	const uploadedAt = role === 'child' ? null : (item?.assignmentMeta?.dateUploaded ?? null);

	// The roster is the source of truth for who covers the unit. If it names someone else now,
	// this conversation is history: readable, but no longer the place to send anything.
	const rosterAgentId = item?.isAssigned ? (item.agent?.id ?? null) : null;
	const reassigned = role === 'parent' && coverageState === 'ready' && !!counterpart && rosterAgentId !== counterpart.coreUserId;
	const replacement = reassigned && item?.agent ? item.agent : null;
	const inactive = role === 'parent' && !!item?.agent && item.agent.id === counterpart?.coreUserId && !item.agent.isActive;

	// ── Presence ────────────────────────────────────────────────────────────────────────────
	const [presence, setPresence] = useState<{ lastSeenAt: string | null; inThreadKey: string | null } | null>(null);
	const [presenceLoading, setPresenceLoading] = useState(true);
	const counterpartId = counterpart?.coreUserId ?? 0;
	useEffect(() => {
		if (!counterpartId || !electionId) return;
		let cancelled = false;
		const load = () => {
			void fetchPeerPresence(electionId, [counterpartId]).then((rows) => {
				if (cancelled) return;
				const row = rows.find((r) => r.agentUserId === counterpartId);
				setPresence(row ? { lastSeenAt: row.lastSeenAt, inThreadKey: row.inThreadKey } : null);
				setPresenceLoading(false);
			});
		};
		load();
		const timer = window.setInterval(load, PRESENCE_REFRESH_MS);
		return () => {
			cancelled = true;
			window.clearInterval(timer);
		};
	}, [counterpartId, electionId]);

	// ── Actions ─────────────────────────────────────────────────────────────────────────────
	const [sheetOpen, setSheetOpen] = useState(false);
	const closeSheet = useCallback(() => setSheetOpen(false), []);
	const copyProps = useCopy();

	// Not every entry point knows the counterpart's level (the Help bubble, inbox summaries and the
	// deep link carry only the code), so fall back to the level the code implies — one label everywhere.
	const counterpartLevel =
		counterpart?.level || (counterpart?.entityCode ? hierarchyLevelOfCode(counterpart.entityCode) : undefined);

	const chatCounterpart: ChatCounterpart = useMemo(
		() => ({
			kind: 'peer',
			name: counterpart?.name ?? 'Agent',
			roleLabel: roleLabelFor(counterpartLevel, counterpart?.role ?? 'child'),
		}),
		[counterpart?.name, counterpartLevel, counterpart?.role]
	);

	if (!data || !counterpart || !rsProps) return null;
	if (!thread) return <AgentSkeleton />;

	const roleLabel = roleLabelFor(counterpartLevel, counterpart.role);
	const phone = counterpart.phone?.trim();
	const wa = formatPhoneForWa(phone);

	const openCoverage = (initialTab?: 'agent' | 'result') => {
		// Opened FROM the coverage drawer: Back is that drawer, so go back rather than stacking
		// a second copy of the same panel on top of this one.
		if (data.origin === 'coverage' && !initialTab) {
			rsProps.removeRightSectionHistory();
			return;
		}
		const coverageData: IChildAgentData = {
			...((data.coverageData as Partial<IChildAgentData> | undefined) ?? {}),
			entityCode: counterpart.entityCode ?? '',
			entityName: (data.coverageData?.entityName as string | undefined) ?? counterpart.entityCode,
			level: (data.coverageData?.level as string | undefined) ?? counterpart.level ?? '',
			electionId,
			initialTab,
		};
		rsProps.addRightSectionHistory();
		rsProps.callSection({ action: 'view', component: 'child-agent', title: 'Agent Coverage', data: coverageData });
	};

	const openOwnResult = () => {
		if (!electionOfficial?.assignment?.isCompleted) return;
		rsProps.addRightSectionHistory();
		rsProps.callSection({
			action: 'view',
			component: 'view-submitted-result',
			title: `View Result - ${electionOfficial.election}`,
			data: {
				electionName: electionOfficial.election,
				electoralDivision: { name: electionOfficial.assignment.name, value: electionOfficial.assignment.id },
				referenceId: electionOfficial.referenceId,
				id: electionOfficial.id,
			},
			resultType: toResultType(electionOfficial.assignment.resultType) ?? undefined,
		});
	};

	const escalate = () => {
		const lastFromCounterpart = [...thread.messages]
			.reverse()
			.find((m) => !isOwnMessage(m, { me, kind: 'peer', ownClientIds: thread.ownClientIds }) && m.kind !== 'deleted');
		const seedDraft = escalationDraft({
			role,
			unitCode: unitCode ?? 'this unit',
			counterpartName: counterpart.name,
			counterpartRoleLabel: roleLabel,
			lastFromCounterpart: lastFromCounterpart ?? null,
		});
		const payload: ISupportChatPanelData = { seedDraft };
		rsProps.addRightSectionHistory();
		rsProps.callSection({ action: 'view', component: 'support-chat', title: 'Support desk', data: payload });
	};

	// The child has no coverage drawer for a parent, so the item is omitted, not disabled.
	const coverageItem: IActionSheetItem = {
		key: 'coverage',
		label: 'Open unit coverage',
		onSelect: () => {
			closeSheet();
			openCoverage();
		},
		disabled: !counterpart.entityCode,
		hint: counterpart.entityCode ? undefined : 'No unit on file for this agent',
	};

	const sheetItems: IActionSheetItem[] = [
		{
			key: 'call',
			label: 'Call',
			onSelect: () => {
				closeSheet();
				if (phone) window.location.href = `tel:${phone}`;
			},
			disabled: !phone,
			hint: phone ? undefined : 'No phone number on file',
		},
		{
			key: 'whatsapp',
			label: 'WhatsApp',
			onSelect: () => {
				closeSheet();
				if (wa) window.open(`https://wa.me/${wa}`, '_blank', 'noopener,noreferrer');
			},
			disabled: !wa,
			hint: wa ? undefined : 'No phone number on file',
		},
		...(role === 'parent' ? [coverageItem] : []),
		{
			key: 'copy',
			label: 'Copy unit code',
			onSelect: () => {
				closeSheet();
				if (unitCode) copyProps.copy(unitCode);
			},
			disabled: !unitCode,
			hint: unitCode ? unitCode : 'No unit code on file',
		},
		{
			key: 'escalate',
			label: 'Escalate to support desk',
			onSelect: () => {
				closeSheet();
				escalate();
			},
			hint: 'Opens the desk with a draft you can edit before sending',
		},
	];

	const chip = (() => {
		if (role === 'child') {
			const c = unitStateChip(unitState);
			return { label: `Your result · ${c.label}`, tone: c.tone, unitCode: unitCode ?? undefined };
		}
		if (coverageState === 'error') return { label: 'Result state unavailable', tone: 'neutral' as const };
		if (!coverage) return { label: 'Checking…', tone: 'neutral' as const };
		if (coverage.state === 'unassigned') return { label: 'No agent assigned', tone: 'neutral' as const };
		if (coverage.state === 'agentUnavailable') return { label: 'Account unavailable', tone: 'warning' as const };
		if (item?.assignmentMeta?.hasConflict && coverage.state === 'conflict')
			return { label: 'Disputed assignment', tone: 'danger' as const };
		return unitStateChip(unitState, uploadedAt);
	})();

	const banner: React.ReactNode = (
		<>
			{reassigned ? (
				<div className='sc-banner sc-banner--unavailable sc-banner--stack'>
					{reassignedNotice(counterpart.name, unitCode ?? 'this unit', replacement ? agentDisplayName(replacement) : null)}
					{replacement ? (
						<button
							type='button'
							className='sc-banner__action'
							onClick={() => {
								const next: IAgentChatPanelData = {
									counterpart: {
										coreUserId: replacement.id,
										name: agentDisplayName(replacement),
										entityCode: counterpart.entityCode,
										level: counterpart.level,
										role: 'child',
										phone: replacement.phoneNumber?.trim() || undefined,
										email: replacement.email?.trim() || undefined,
									},
									electionId,
									origin: data.origin,
									coverageData: data.coverageData,
								};
								rsProps.callSection({ action: 'view', component: 'agent-chat', title: next.counterpart.name, data: next });
							}}
						>
							Message {agentDisplayName(replacement).split(' ')[0]}
						</button>
					) : null}
				</div>
			) : null}
			{inactive ? <div className='sc-banner sc-banner--offline'>{INACTIVE_HINT}</div> : null}
		</>
	);

	return (
		<div className='support-chat agent-chat'>
			<ChatHeaderStrip
				counterpart={counterpart}
				roleLabel={roleLabel}
				presence={presenceLine(
					presence && {
						lastSeenAt: presence.lastSeenAt,
						inThisThread: !!thread.threadKey && presence.inThreadKey === thread.threadKey,
					},
					presenceLoading
				)}
				chip={chip}
				onChip={
					role === 'parent' ? () => openCoverage('result') : electionOfficial?.assignment?.isCompleted ? openOwnResult : undefined
				}
				onMore={() => setSheetOpen(true)}
			/>
			<ChatConversation
				// A different person is a different conversation: reset composer mode, chip
				// insertion and the opener when the reassignment banner swaps the counterpart.
				key={counterpart.coreUserId}
				thread={thread}
				counterpart={chatCounterpart}
				emptyState={emptyStateFor({
					role,
					counterpartName: counterpart.name,
					counterpartLevel: roleLabel.replace(/ agent$/i, '').toLowerCase(),
					unitCode,
					unitState,
					uploadedAt,
				})}
				preamble={contextLine(unitCode, unitState)}
				banner={banner}
				quickMessages={quickMessages(role, unitState, unitCode)}
				composerDisabled={reassigned}
			/>
			{sheetOpen ? (
				<ActionSheet
					label={`Actions for ${counterpart.name}`}
					items={sheetItems}
					onClose={closeSheet}
				/>
			) : null}
		</div>
	);
};

export default AgentChatPanel;
